// @tennis-matches/schleifchen-core/routing/flow
import { clampInt } from "@tennis-matches/core/utils";
import { __asInt } from "@tennis-matches/core/utils";
import { getUrlCode as __getUrlCode } from "@tennis-matches/core/utils";

/**
 * Ziel-Flow (Wizard, nur vorwärts):
 * home -> matchmixer-setup -> matchmixer-schedule -> matchmixer -> matchmixer-entry -> matchmixer -> ... -> matchmixer-finish
 *
 * WICHTIG (Share / Direct-open):
 * - /matchmixer-finish?code=... ist ein BOOTSTRAP-Entry.
 *   Die Finish-Page lädt per Code aus der DB und entscheidet dann per Redirect,
 *   wo der korrekte Einstieg ist. flow.js darf hier NICHT vorzeitig umleiten.
 */

// ------------------------------
// Query / route helpers (pathname + hash)
// ------------------------------
function mergeQuery(base, override) {
  const a = new URLSearchParams(String(base || "").replace(/^\?/, ""));
  const b = new URLSearchParams(String(override || "").replace(/^\?/, ""));
  for (const [k, v] of b.entries()) a.set(k, v);
  const s = a.toString();
  return s ? `?${s}` : "";
}

function __getQueryParams() {
  const search = String(window.location.search || "");
  const hash = String(window.location.hash || "");
  const hashQuery = hash.includes("?") ? hash.split("?")[1] : "";
  const merged = mergeQuery(search, hashQuery);
  try {
    return new URLSearchParams(merged.replace(/^\?/, ""));
  } catch (_) {
    return new URLSearchParams("");
  }
}

// __getUrlCode is now imported from commonHelpers.js

function __getWantNew() {
  try {
    const params = __getQueryParams();
    const v = String(params.get("new") || "").toLowerCase();
    return v === "1" || v === "true" || v === "yes";
  } catch (_) {
    return false;
  }
}

function __ttParseRoute() {
  const pathname = String(window.location.pathname || "");
  if (pathname && pathname !== "/") {
    const path = pathname.replace(/^\/+/, "");
    const r = path.split("/")[0].trim();
    if (r) return r;
  }

  const h = String(window.location.hash || "").trim();
  const raw = h.startsWith("#") ? h.slice(1) : h;
  const route0 = raw.split("?")[0].trim().replace(/^\/+/, "");
  return route0 || "home";
}

export const PHASE = {
  IDLE: "idle",
  COUNTDOWN: "countdown",
  ROUND: "round",
  ENTRY: "entry",
  FINISHED: "finished", // compat
  FINISH: "finish",
};

const VALID = new Set(Object.values(PHASE));

// __asInt is now imported from commonHelpers.js

function __getSchleifchenRounds(state) {
  const st = state && typeof state === "object" ? state : {};
  const mod = st?.modules?.schleifchen || {};
  const t = mod.tournament || {};
  const rounds = __asInt(t?.rounds, 0);
  return rounds > 0 ? rounds : 0;
}

/**
 * If the state indicates we're beyond the last round, force FINISH.
 * Also clamps ui.round into valid range when not finished.
 */
function __ensureFinishPhase(state) {
  const st = state && typeof state === "object" ? state : {};
  const mod = st?.modules?.schleifchen || {};
  const ui = mod.ui || {};

  const rounds = __getSchleifchenRounds(st);
  if (!rounds) return st;

  const cur = __asInt(ui?.round, 1);
  const done = __asInt(ui?.completedRound, 0);
  const finished = done >= rounds || cur > rounds;

  if (!finished) {
    const clamped = Math.min(Math.max(cur, 1), rounds);
    if (clamped === cur) return st;
    return {
      ...st,
      modules: {
        ...st.modules,
        schleifchen: {
          ...mod,
          ui: { ...ui, round: clamped },
        },
      },
    };
  }

  const nextUi = {
    ...ui,
    round: rounds,
    completedRound: rounds,
    phase: PHASE.FINISH,
  };

  return {
    ...st,
    modules: {
      ...st.modules,
      schleifchen: {
        ...mod,
        ui: nextUi,
      },
    },
  };
}

export function getUi(state) {
  state = __ensureFinishPhase(state);
  const ui = state?.modules?.schleifchen?.ui || {};
  return {
    round: clampInt(ui.round ?? 1, 1, 999, 1),
    completedRound: clampInt(ui.completedRound ?? 0, 0, 999, 0),
    phase: VALID.has(ui.phase) ? ui.phase : PHASE.IDLE,
  };
}

export function hasSchedule(state) {
  state = __ensureFinishPhase(state);
  const sch = state?.modules?.schleifchen?.schedule;
  return Array.isArray(sch) && sch.length > 0;
}

export function getRouteFromHash() {
  return __ttParseRoute();
}

export function getNormalizedHashRoute(state) {
  const cur = getRouteFromHash();
  return normalizeRoute(cur, state);
}

/* ------------------------------------------------------------------ */
/* finish detection (LOCAL / state-based)                               */
/* NOTE: DB truth for ?code=... is handled in schleifchenFinishPage.jsx  */
/* ------------------------------------------------------------------ */

function __metaSaysFinished(state, ui) {
  const mod = state?.modules?.schleifchen || {};
  const tournament = mod.tournament || {};
  const meta = tournament.meta || {};
  const phase = String(meta?.phase || "").toLowerCase();
  const uiPhase = String(ui?.phase || "").toLowerCase();
  return (
    phase === "finished" ||
    phase === "finish" ||
    uiPhase === "finish" ||
    uiPhase === "finished"
  );
}

function __allRoundsDone(state, ui) {
  const mod = state?.modules?.schleifchen || {};
  const schedule = mod.schedule || [];
  const totalRounds = Array.isArray(schedule) ? schedule.length : 0;
  const done = Number(ui?.completedRound || 0);
  return totalRounds > 0 && done >= totalRounds;
}

/**
 * Local definition of "finished":
 * - either meta says finished (local state)
 * - OR ui.phase is finish AND all rounds done
 *
 * This is used ONLY when there is NO ?code=... bootstrap.
 */
function __isActuallyFinishedLocal(state, ui) {
  if (__metaSaysFinished(state, ui)) return true;

  const uiIsFinish = ui?.phase === PHASE.FINISH || ui?.phase === PHASE.FINISHED;

  return uiIsFinish && __allRoundsDone(state, ui);
}

/**
 * Check if tournament is still in "planned" phase (not started yet).
 * Tournament is planned if:
 * - meta.phase is "planned" or empty/null
 * - AND ui.phase is "idle"
 * - AND no rounds have been completed
 * - AND current round is 1 or less
 */
function __isPlanned(state, ui) {
  const mod = state?.modules?.schleifchen || {};
  const tournament = mod.tournament || {};
  const meta = tournament.meta || {};
  const phase = String(meta?.phase || "").toLowerCase();

  // If meta says running/finished, it's not planned
  if (phase === "running" || phase === "finished" || phase === "finish") {
    return false;
  }

  // Check UI state - if phase is not idle, it's started
  if (ui?.phase !== PHASE.IDLE) {
    return false;
  }

  // Check if any rounds have been completed or started
  if (Number(ui?.completedRound || 0) > 0) {
    return false;
  }

  if (Number(ui?.round || 1) > 1) {
    return false;
  }

  // Also check timer state
  const timer = state?.timer || {};
  if (timer.isRunning) {
    return false;
  }

  return true;
}

/* ------------------------------------------------------------------ */
/* normalization / guards                                               */
/* ------------------------------------------------------------------ */

// Idempotency guard: duplicate calls with same route+code within 250ms return cached result
// (avoids multiple START logs from useMemo + Breadcrumb + StrictMode)
let __normKey = "";
let __normTs = 0;
let __normResult = null;
const __NORM_MS = 250;

/**
 * Guards enforce your forward-only wizard flow:
 * home -> setup -> schedule -> execution -> entry -> execution -> finish
 *
 * Additionally:
 * - "matchmixer-results" => "matchmixer-entry" (compat alias)
 * - "main" => "matchmixer"
 */
export function normalizeRoute(route, state) {
  // Accept route string only
  route = String(route ?? "")
    .trim()
    .replace(/^\/+/, "");
  route = route.split("?")[0]; // IMPORTANT: strip query if someone passes it in
  if (!route) route = "home";

  // compatibility
  if (route === "main") route = "matchmixer";

  // Legacy route keys (Bookmarks / alte Links) → kanonische MatchMixer-Keys
  const LEGACY_ROUTE_KEYS = {
    schleifchen: "matchmixer",
    "schleifchen-setup": "matchmixer-setup",
    "schleifchen-schedule": "matchmixer-schedule",
    "schleifchen-entry": "matchmixer-entry",
    "schleifchen-results": "matchmixer-results",
    "schleifchen-round-config": "matchmixer-round-config",
    "schleifchen-finish": "matchmixer-finish",
  };
  if (LEGACY_ROUTE_KEYS[route]) route = LEGACY_ROUTE_KEYS[route];

  state = state && typeof state === "object" ? state : {};
  const urlCode = __getUrlCode();
  const key = `${route}|${urlCode || ""}`;
  const now = Date.now();
  if (__normKey === key && now - __normTs < __NORM_MS) return __normResult;
  const __setAndReturn = (v) => {
    __normKey = key;
    __normTs = Date.now();
    __normResult = v;
    return v;
  };

  const ui = getUi(state);
  const wantNew = __getWantNew();

  const isSetup = route === "matchmixer-setup";
  const isSetupPlayers = route === "matchmixer-setup-players";
  const isSchedule = route === "matchmixer-schedule";
  const isExec = route === "matchmixer";
  const isEntry = route === "matchmixer-entry";
  const isRoundConfig = route === "matchmixer-round-config";
  const isFinish = route === "matchmixer-finish";
  const isResults = route === "matchmixer-results";
  const isRegistration = route === "matchmixer-anmelden";

  const hasSch = hasSchedule(state);

  // Turnier-Anmeldung (vor Spielplan): nie in Durchführung/Setup umleiten.
  if (isRegistration) {
    return __setAndReturn("matchmixer-anmelden");
  }

  // Teilnehmer-Editor ist Teil des Setups (Vorbereitung). Nie in die Durchführung
  // schicken — sonst erscheint „noch kein Spielplan“, obwohl bewusst noch keiner existiert.
  if (isSetupPlayers) {
    return __setAndReturn("matchmixer-setup-players");
  }

  // Setup mit Code ist der Einstieg zum Weiterbearbeiten (Spieler ergänzen),
  // auch wenn Persist noch eine andere Turnier-Phase hat. Ob das Turnier schon
  // läuft, entscheidet die Setup-Seite anhand der DB — nicht der Host-Guard.
  if (urlCode && isSetup) {
    return __setAndReturn("matchmixer-setup");
  }

  // CRITICAL: If code is in URL and route is schedule, ALWAYS allow it (even if state is empty)
  // This must be checked BEFORE any other logic to prevent redirects during data loading
  // This is the FIRST check - before NEW TOURNAMENT MODE, before DB BOOTSTRAP MODE, before everything
  if (urlCode && isSchedule) {
    return __setAndReturn("matchmixer-schedule");
  }

  // ------------------------------------------------------------
  // NEW TOURNAMENT MODE (?new=1)
  // - If ?new=1 is set AND route is setup, ALWAYS stay on setup (single path: new → setup only).
  // - Must return here so we never use stale state (e.g. phase=finish) to redirect to finish.
  // ------------------------------------------------------------
  if (wantNew && isSetup) {
    return __setAndReturn("matchmixer-setup");
  }
  // ------------------------------------------------------------
  // DB BOOTSTRAP MODE (code present)
  // - DB decides status/permission; local wizard must NOT override.
  // - Setup/Entry are local-only and must never be entry points with code.
  // - EXCEPTION 1: Allow setup if tournament is still "planned" (not started yet)
  // - EXCEPTION 2: Allow schedule if URL explicitly requests it (direct link with code) OR if tournament is "planned" and schedule exists (for breadcrumb navigation)
  // - EXCEPTION 3: Allow results page if URL explicitly requests it (direct link with code) - check after loading
  // - NOTE: ?new=1 is handled above and skips this mode
  // ------------------------------------------------------------
  else if (urlCode && !(wantNew && isSetup)) {
    const isPlanned = __isPlanned(state, ui);
    // CRITICAL: Get phase from DB (meta or tournament.dbPhase)
    const mod = state?.modules?.schleifchen || {};
    const tournament = mod.tournament || {};
    const meta = tournament.meta || {};
    const dbPhase = tournament.dbPhase || meta?.phase || "";
    const phase = String(dbPhase || "").toLowerCase();

    if (isFinish) return __setAndReturn("matchmixer-finish");

    // Results route: If URL explicitly requests results (direct link with code), always allow it.
    // The results page will handle checking if tournament is running/finished and redirect if needed.
    // CRITICAL: Always allow results page if URL has code - let the page check itself
    // This prevents redirect loop where we redirect before state is loaded
    if (isResults) {
      // ALWAYS allow results page if URL has code - the page will check if it's valid
      // This ensures we don't redirect away before the state is fully loaded
      // The SchleifchenResultsPage will call redirectIfRouteMismatch after state is loaded
      return __setAndReturn("matchmixer-results");
    }

    // Schedule route: If URL explicitly requests schedule (direct link with code), always allow it.
    // This ensures direct links like matchmixer-schedule?code=xxx work correctly.
    // Also allow if tournament is still "planned" and schedule exists (for breadcrumb navigation).
    // CRITICAL: Always allow schedule page if code is in URL, even if state is not loaded yet
    // This prevents redirect to setup/home before data is loaded
    if (isSchedule) {
      // ALWAYS allow if URL explicitly requests schedule (direct link with code)
      // The schedule page will handle loading the tournament data
      // Don't check hasSchedule here - data might not be loaded yet!
      return __setAndReturn("matchmixer-schedule");
    }

    // Round config route: If URL explicitly requests round-config (direct link with code), always allow it.
    // This ensures navigation to round-config page works correctly when needed.
    // The round-config page will handle checking if it's valid and redirect if needed.
    if (isRoundConfig) {
      // ALWAYS allow round-config page if URL has code - the page will check if it's valid
      // This ensures we don't redirect away before the state is fully loaded
      // The SchleifchenRoundConfigPage will call redirectIfRouteMismatch after state is loaded
      return __setAndReturn("matchmixer-round-config");
    }

    // Exec route: Always allow if URL explicitly requests it (direct link with code)
    // This allows users to navigate to execution page even if tournament is still "planned"
    // (e.g., from schedule page "Weiter" button)
    if (isExec) return __setAndReturn("matchmixer");

    // Allow setup if ?new=1 is present (explicit new tournament creation)
    if (isSetup && wantNew) return __setAndReturn("matchmixer-setup");

    // Allow setup if tournament is still "planned" (not started yet)
    if (isSetup && isPlanned) return __setAndReturn("matchmixer-setup");

    // Hard-block wizard-only steps in code-mode (only if tournament is running/finished):
    // NOTE: Round-config is now handled above, so it's not blocked here anymore
    // NOTE: setup-players is handled above and must stay in Vorbereitung.
    if (isSetup || isEntry) return __setAndReturn("matchmixer");

    // For any other matchmixer-* route, prefer exec to avoid accidental setup loops
    // BUT: exclude matchmixer-results which was already handled above
    if (route.startsWith("matchmixer-") && !isResults)
      return __setAndReturn("matchmixer");

    return __setAndReturn(route);
  }

  // ------------------------------------------------------------
  // LOCAL WIZARD MODE (no code)
  // ------------------------------------------------------------

  // ENTRY is mandatory: if UI is in ENTRY phase, force the entry page.
  // BUT: If ?new=1 is set, ignore ENTRY phase to allow new tournament creation
  if (ui.phase === PHASE.ENTRY && !wantNew) {
    return __setAndReturn(hasSch ? "matchmixer-entry" : "matchmixer-setup");
  }

  // If local state reached FINISH, lock it. No going back.
  // EXCEPT when wantNew: user explicitly started a new tournament → stay on setup.
  if ((ui.phase === PHASE.FINISH || ui.phase === PHASE.FINISHED) && !wantNew) {
    return __setAndReturn("matchmixer-finish");
  }

  // Finish route rules (LOCAL)
  if (isFinish) {
    if (!hasSch) return __setAndReturn("matchmixer-setup");
    return __setAndReturn(
      __isActuallyFinishedLocal(state, ui) ? "matchmixer-finish" : "matchmixer",
    );
  }

  // Setup route rules (LOCAL)
  if (isSetup) {
    // If ?new=1 is set, ALWAYS allow setup (ignore existing schedule/state)
    // This ensures "New Tournament" button creates a truly new tournament
    if (wantNew) {
      return __setAndReturn("matchmixer-setup");
    }

    if (hasSch) {
      const timer = state?.timer || {};
      const started =
        ui.phase !== PHASE.IDLE ||
        ui.completedRound > 0 ||
        ui.round > 1 ||
        !!timer.isRunning;
      return __setAndReturn(started ? "matchmixer" : "matchmixer-schedule");
    }
    return __setAndReturn("matchmixer-setup");
  }

  // Schedule route rules (LOCAL)
  // CRITICAL: If code is in URL, this should have been handled in DB BOOTSTRAP MODE above
  // Only apply LOCAL rules if no code is present (wizard mode)
  if (isSchedule) {
    // If code is in URL, this should have been handled above - but double-check to prevent redirect
    const urlCodeCheck = __getUrlCode();
    if (urlCodeCheck) {
      // Code in URL - allow schedule page (data will be loaded by useTournamentLoader)
      return __setAndReturn("matchmixer-schedule");
    }

    // No code - LOCAL WIZARD MODE: check if schedule exists
    if (!hasSch) return __setAndReturn("matchmixer-setup");
    const timer = state?.timer || {};
    const started =
      ui.phase !== PHASE.IDLE ||
      ui.completedRound > 0 ||
      ui.round > 1 ||
      !!timer.isRunning;
    return __setAndReturn(started ? "matchmixer" : "matchmixer-schedule");
  }

  // Entry route rules (LOCAL)
  if (isEntry) {
    if (!hasSch) return __setAndReturn("matchmixer-setup");
    return __setAndReturn(
      ui.phase === PHASE.ENTRY ? "matchmixer-entry" : "matchmixer",
    );
  }

  // Round config route rules (LOCAL)
  if (isRoundConfig) {
    if (!hasSch) return __setAndReturn("matchmixer-setup");

    const completedRound = Number(ui.completedRound || 0);
    const currentRound = Number(ui.round || 1);

    // Check if next round exists in schedule
    const mod = state?.modules?.schleifchen || {};
    const schedule = Array.isArray(mod.schedule) ? mod.schedule : [];
    const nextRoundIndex = currentRound - 1;
    const nextRoundExists =
      nextRoundIndex >= 0 &&
      nextRoundIndex < schedule.length &&
      Array.isArray(schedule[nextRoundIndex]) &&
      schedule[nextRoundIndex].length > 0;

    // Check if next round has a resting player
    const participationStatus = mod.participationStatus || {};
    const nextRoundParticipationStatus =
      participationStatus[currentRound] || {};
    const hasRestingPlayer = Object.values(nextRoundParticipationStatus).some(
      (status) => status === "resting",
    );

    // Allow if next round needs configuration OR already exists OR has resting player
    const isNextRound = currentRound === completedRound + 1;

    if (isNextRound || nextRoundExists || hasRestingPlayer) {
      return __setAndReturn("matchmixer-round-config");
    }

    return __setAndReturn("matchmixer");
  }

  // Results route rules (LOCAL)
  if (isResults) {
    if (!hasSch) return __setAndReturn("matchmixer-setup");
    const mod = state?.modules?.schleifchen || {};
    const hasResults = mod.results && Object.keys(mod.results || {}).length > 0;
    const schedule = mod.schedule || [];
    const scheduleLen = Array.isArray(schedule) ? schedule.length : 0;
    const completedRound = Number(ui.completedRound || 0);
    const timer = state?.timer || {};

    // Tournament is running if:
    // - phase is ROUND (active round)
    // - OR has results and not all rounds completed
    // - OR timer is running
    const isRunning =
      ui.phase === PHASE.ROUND ||
      ui.phase === PHASE.COUNTDOWN ||
      (hasResults && completedRound > 0 && completedRound < scheduleLen) ||
      !!timer.isRunning;

    // Tournament is finished if:
    // - phase is FINISH or FINISHED
    // - OR all rounds have results
    const isFinished =
      ui.phase === PHASE.FINISH ||
      ui.phase === PHASE.FINISHED ||
      (hasResults && completedRound > 0 && completedRound >= scheduleLen);

    if (isRunning || isFinished) return __setAndReturn("matchmixer-results");
    // If not running/finished, redirect to appropriate page
    if (ui.phase === PHASE.IDLE && !hasResults)
      return __setAndReturn("matchmixer-schedule");
    return __setAndReturn("matchmixer");
  }

  // Execution route rules (LOCAL)
  if (isExec) {
    if (!hasSch) return __setAndReturn("matchmixer-setup");
    if (__isActuallyFinishedLocal(state, ui))
      return __setAndReturn("matchmixer-finish");
    return __setAndReturn("matchmixer");
  }

  return __setAndReturn(route);
}
